import { gql } from '@apollo/client';
import { useOffsetPaginatedQuery } from '@everlutionsk/ui-apollo';
import { Box, Stack, Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { StatusBadge } from '../components/coreComponents/badge';
import { Button } from '../components/coreComponents/button';
import { Input } from '../components/coreComponents/input';
import { SelectInput, SelectOption } from '../components/coreComponents/selectInput';
import { MainWrapper, MainWrapperHeader } from '../components/mainPageWrapper';
import { Table } from '../components/microLayouts/table';
import { AdminRole, AdminUserStatus } from '../graphql/types';
import { InviteAdminDrawer } from './inviteAdminDrawer';

const ALL = 'all';

const roleOptions: ReadonlyArray<SelectOption<typeof ALL | AdminRole>> = [
  { label: 'All roles', value: ALL },
  { label: 'Admin', value: 'admin' },
  { label: 'Superadmin', value: 'superAdmin' }
];

const statusOptions: ReadonlyArray<SelectOption<typeof ALL | AdminUserStatus>> = [
  { label: 'All statuses', value: ALL },
  { label: 'Draft', value: 'draft' },
  { label: 'Active', value: 'active' },
  { label: 'Revoked', value: 'revoked' }
];

// Administrators section: lists control-plane admins (draft + active) and the
// Invite drawer. Draft admins can't sign in until login credentials are
// granted via the two-person flow on their detail page.
export function Administrators() {
  const navigate = useNavigate();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [role, setRole] = useState<typeof ALL | AdminRole>(ALL);
  const [status, setStatus] = useState<typeof ALL | AdminUserStatus>(ALL);
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');

  const filterInput = useMemo(
    () => ({
      ...(role !== ALL ? { roles: [role] } : {}),
      ...(status !== ALL ? { statuses: [status] } : {}),
      ...(search.trim() !== '' ? { search: search.trim() } : {})
    }),
    [role, status, search]
  );

  const { data, pagination, loading } = useOffsetPaginatedQuery(query, {
    variables: page => ({ input: { page, ...filterInput } }),
    total: data => data.adminUserMany.total
  });

  const items = data?.adminUserMany.items ?? [];

  return (
    <MainWrapper>
      <MainWrapperHeader
        title="Administrators"
        description="Control-plane admins and superadmins"
        primaryAction={
          <Button variant="primary" onClick={() => setInviteOpen(true)}>
            Invite administrator
          </Button>
        }
      />

      <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap sx={{ mb: 5 }}>
        <Box sx={{ width: 230 }}>
          <Input
            label="Search"
            value={searchDraft}
            placeholder="Name or email, then Enter"
            onChange={event => setSearchDraft(event.target.value)}
            onBlur={() => setSearch(searchDraft)}
            onKeyDown={event => {
              if (event.key === 'Enter') setSearch(searchDraft);
            }}
          />
        </Box>
        <Box sx={{ width: 180 }}>
          <SelectInput label="Role" value={role} onChange={setRole} options={roleOptions} />
        </Box>
        <Box sx={{ width: 180 }}>
          <SelectInput label="Status" value={status} onChange={setStatus} options={statusOptions} />
        </Box>
      </Stack>

      <Table
        items={loading ? undefined : items}
        pagination={pagination}
        rowKey={row => row.id}
        rowProps={row => ({
          onClick: () => navigate(`/administrators/${row.id}/overview`),
          sx: { cursor: 'pointer' }
        })}
        columns={[
          {
            label: 'Name',
            render: row => (
              <Typography variant="body2">
                {`${row.firstName ?? ''} ${row.lastName ?? ''}`.trim() || '—'}
              </Typography>
            )
          },
          {
            label: 'Email',
            render: row => <Typography variant="body2">{row.email}</Typography>
          },
          {
            label: 'Role',
            render: row => <RoleChip role={row.role} />
          },
          {
            label: 'Status',
            render: row => <StatusChip status={row.status} />
          },
          {
            label: 'Created',
            render: row => (
              <Typography variant="body2">{new Date(row.createdAt).toLocaleString()}</Typography>
            )
          }
        ]}
        noDataPlaceholder="No administrators."
      />

      <InviteAdminDrawer open={inviteOpen} onClose={() => setInviteOpen(false)} />
    </MainWrapper>
  );
}

function RoleChip({ role }: { role: AdminRole }) {
  return (
    <StatusBadge
      tone={role === 'superAdmin' ? 'info' : 'default'}
      label={role === 'superAdmin' ? 'Superadmin' : 'Admin'}
    />
  );
}

function StatusChip({ status }: { status: AdminUserStatus }) {
  if (status === 'active') return <StatusBadge tone="success" label="Active" />;
  if (status === 'revoked') return <StatusBadge tone="error" label="Revoked" />;
  return <StatusBadge tone="default" label="Draft" />;
}

const query = gql<AdminUserManyGQL>`
  query AdminUserMany($input: AdminUserManyInput) {
    adminUserMany(input: $input) {
      total
      items {
        id
        email
        firstName
        lastName
        role
        status
        createdAt
      }
    }
  }
`;
